import { ArrowLeft, Phone, Search, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../../config/axios-conf';
import { getSocket } from '../../../services/socket.service';
import styles from '../../../assets/scss/pages/EmployeeList.module.scss';
import { PERMISSIONS } from '../../../config/permissions';
import { usePermissions } from '../../../hooks/usePermissions';
import { formatCurrency, formatPhoneNumber } from '../../../utils/formatters';

const AVATAR_COLORS = ['#0098F6', '#22C55E', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316'];
function getAvatarColor(name) {
    let hash = 0;
    for (let i = 0; i < (name || '').length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function fmtDateTime(str) {
    if (!str) return '—';
    const d = new Date(str);
    return d.toLocaleDateString('vi-VN') + ' ' + d.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' });
}

function normalize(str) {
    return (str || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/đ/g, 'd').replace(/Đ/g, 'D').replace(/\s+/g, '').toLowerCase();
}

const STATUS_MAP = {
    pending: { label: 'Chưa thanh toán', color: '#EF4444', bg: '#FFF5F5' },
    partial: { label: 'Thanh toán một phần', color: '#F59E0B', bg: '#FFFCF0' },
};

const TYPE_MAP = {
    new: { label: 'Mới', color: '#0098F6', bg: '#E6F4FF' },
    vip: { label: 'VIP', color: '#F59E0B', bg: '#FFFCF0' },
    normal: { label: 'Thường', color: '#22C55E', bg: '#F3FEF7' },
};

export default function ServingBills() {
    const navigate = useNavigate();
    const { hasPermission } = usePermissions();
    const canViewDetail = hasPermission(PERMISSIONS.BILL.VIEW_DETAIL);
    const canDelete = hasPermission(PERMISSIONS.BILL.DELETE);

    const [bills, setBills] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterStatus, setFilterStatus] = useState('all');
    const [search, setSearch] = useState('');
    const [toastMsg, setToastMsg] = useState(null);

    const showToast = useCallback((type, text, duration = 1500) => {
        setToastMsg({ type, text });
        setTimeout(() => setToastMsg(null), duration);
    }, []);

    const fetchBills = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get('/bills/billAll', { params: { limit: 1000, page: 1 } });
            const allBills = data.data || data.bills || [];
            const serving = allBills.filter((b) => b.status === 'pending' || b.status === 'partial');
            serving.sort((a, b) => new Date(b.checkoutTime) - new Date(a.checkoutTime));
            setBills(serving);
        } catch { setBills([]); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { fetchBills(); }, [fetchBills]);

    // Socket realtime
    useEffect(() => {
        const socket = getSocket();
        if (!socket) return;

        const onBillUpsert = (bill) => {
            if (bill.status === 'pending' || bill.status === 'partial') {
                setBills((prev) => {
                    const idx = prev.findIndex((b) => b.id === bill.id);
                    let next;
                    if (idx >= 0) {
                        next = [...prev];
                        next[idx] = bill;
                    } else {
                        next = [bill, ...prev];
                    }
                    return next.sort((a, b) => new Date(b.checkoutTime) - new Date(a.checkoutTime));
                });
            } else {
                setBills((prev) => prev.filter((b) => b.id !== bill.id));
            }
        };

        const onBillRemove = (billId) => {
            setBills((prev) => prev.filter((b) => b.id !== billId && b.id !== billId?.id));
        };

        const onConnect = () => fetchBills();

        socket.on('serving.bill.upsert', onBillUpsert);
        socket.on('serving.bill.remove', onBillRemove);
        socket.on('connect', onConnect);

        return () => {
            socket.off('serving.bill.upsert', onBillUpsert);
            socket.off('serving.bill.remove', onBillRemove);
            socket.off('connect', onConnect);
        };
    }, [fetchBills]);

    const handleDelete = async (e, billId) => {
        e.stopPropagation();
        if (!canDelete) return;
        if (!window.confirm('Xoá đơn đang phục vụ này?')) return;
        try {
            await api.delete(`/bills/${billId}`);
            setBills((prev) => prev.filter((b) => b.id !== billId));
            showToast('success', 'Đã xoá đơn');
        } catch (err) {
            showToast('error', err.response?.status === 403 ? 'Không có quyền xoá' : 'Xoá thất bại', 2500);
        }
    };

    // Filter
    const filtered = bills.filter((b) => {
        if (filterStatus !== 'all' && b.status !== filterStatus) return false;
        if (!search.trim()) return true;
        const q = normalize(search);
        return normalize(b.customer?.name).includes(q) || normalize(b.customer?.phone).includes(q);
    });

    const pendingCount = bills.filter((b) => b.status === 'pending').length;
    const partialCount = bills.filter((b) => b.status === 'partial').length;

    return (
        <div className={styles.page}>
            <div className={styles.header}>
                <div className={styles.headerLeft} style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                    <button style={{
                        width: 40, height: 40, border: '1.5px solid #ECEEF2', borderRadius: 10,
                        background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }} onClick={() => navigate(-1)}><ArrowLeft size={20} /></button>
                    <div>
                        <h1 className={styles.title}>Đang phục vụ</h1>
                        <p className={styles.subtitle}>{bills.length} đơn đang xử lý</p>
                    </div>
                </div>
            </div>

            {/* KPI */}
            <div className={styles.kpis}>
                <div className={styles.kpi}>
                    <div className={styles.kpiContent}>
                        <span className={styles.kpiLabel}>Tổng đơn</span>
                        <span className={styles.kpiValue}>{bills.length}</span>
                    </div>
                </div>
                <div className={styles.kpi}>
                    <div className={styles.kpiContent}>
                        <span className={styles.kpiLabel}>Chưa thanh toán</span>
                        <span className={styles.kpiValue} style={{ color: '#EF4444' }}>{pendingCount}</span>
                    </div>
                </div>
                <div className={styles.kpi}>
                    <div className={styles.kpiContent}>
                        <span className={styles.kpiLabel}>Thanh toán 1 phần</span>
                        <span className={styles.kpiValue} style={{ color: '#F59E0B' }}>{partialCount}</span>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
                {[
                    { key: 'all', label: 'Tất cả' },
                    { key: 'pending', label: 'Chưa thanh toán' },
                    { key: 'partial', label: 'Thanh toán 1 phần' },
                ].map((f) => (
                    <button key={f.key} style={{
                        padding: '8px 18px', border: '1.5px solid ' + (filterStatus === f.key ? '#0098F6' : '#ECEEF2'),
                        borderRadius: 999, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                        background: filterStatus === f.key ? '#0098F6' : '#fff',
                        color: filterStatus === f.key ? '#fff' : '#6B7280',
                    }} onClick={() => setFilterStatus(f.key)}>{f.label}</button>
                ))}
                <div style={{ position: 'relative', flex: 1, maxWidth: 280 }}>
                    <Search size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#6B7280' }} />
                    <input style={{
                        width: '100%', height: 38, padding: '0 14px 0 36px', border: '1.5px solid #ECEEF2',
                        borderRadius: 10, fontSize: 13, fontFamily: 'inherit', outline: 'none', background: '#FAFBFC',
                    }} value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Tìm tên, SĐT..." />
                </div>
            </div>

            {/* Table */}
            <div className={styles.tableWrapper}>
                {loading ? (
                    <div className={styles.loading}><div className={styles.spinner} /> Đang tải...</div>
                ) : filtered.length === 0 ? (
                    <div className={styles.empty}>Không có đơn đang phục vụ</div>
                ) : (
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th style={{ width: 45, textAlign: 'center' }}>STT</th>
                                <th>Khách hàng</th>
                                <th>SĐT</th>
                                <th>Loại</th>
                                <th style={{ textAlign: 'right' }}>Tổng tiền</th>
                                <th>Trạng thái</th>
                                <th>Thời gian</th>
                                {canDelete && <th style={{ width: 60 }} />}
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((bill, idx) => {
                                const cust = bill.customer;
                                const status = STATUS_MAP[bill.status] || STATUS_MAP.pending;
                                const custType = TYPE_MAP[cust?.type] || TYPE_MAP.normal;
                                return (
                                    <tr key={bill.id} className={canViewDetail ? styles.rowClickable : undefined}
                                        onClick={() => canViewDetail && navigate(`/admin/bills/create?edit=${bill.id}`)}>
                                        <td style={{ textAlign: 'center', color: '#6B7280' }}>{idx + 1}</td>
                                        <td>
                                            <div className={styles.employeeCell}>
                                                <div className={styles.avatar} style={{ background: getAvatarColor(cust?.name) }}>
                                                    {(cust?.name || '?')[0].toUpperCase()}
                                                </div>
                                                <span className={styles.employeeName}>{cust?.name || 'Khách'}</span>
                                            </div>
                                        </td>
                                        <td>
                                            <div className={styles.phoneCell}>
                                                <Phone size={14} />
                                                {cust?.phone ? formatPhoneNumber(cust.phone) : '—'}
                                            </div>
                                        </td>
                                        <td>
                                            <span style={{
                                                fontSize: 12, fontWeight: 600, padding: '2px 10px', borderRadius: 999,
                                                background: custType.bg, color: custType.color,
                                            }}>{custType.label}</span>
                                        </td>
                                        <td style={{ textAlign: 'right', fontWeight: 600, color: '#22C55E' }}>
                                            {formatCurrency(bill.total || 0)}
                                        </td>
                                        <td>
                                            <span style={{
                                                fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 999,
                                                background: status.bg, color: status.color,
                                            }}>{status.label}</span>
                                        </td>
                                        <td style={{ fontSize: 13, color: '#6B7280' }}>{fmtDateTime(bill.checkoutTime)}</td>
                                        {canDelete && (
                                            <td>
                                                <button style={{
                                                    border: 'none', background: 'none', cursor: 'pointer',
                                                    color: '#6B7280', padding: 4, borderRadius: 6,
                                                }}
                                                    onClick={(e) => handleDelete(e, bill.id)}
                                                    onMouseEnter={(e) => { e.currentTarget.style.color = '#EF4444'; e.currentTarget.style.background = '#FFF5F5'; }}
                                                    onMouseLeave={(e) => { e.currentTarget.style.color = '#6B7280'; e.currentTarget.style.background = 'none'; }}
                                                ><Trash2 size={16} /></button>
                                            </td>
                                        )}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            {toastMsg && (
                <div style={{ position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ background: '#fff', borderRadius: 20, padding: '40px 48px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>
                        <div style={{ width: 60, height: 60, borderRadius: '50%', background: toastMsg.type === 'success' ? '#22C55E' : '#EF4444', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, fontWeight: 700 }}>
                            {toastMsg.type === 'success' ? '✓' : '✕'}
                        </div>
                        <span style={{ fontSize: 16, fontWeight: 600 }}>{toastMsg.text}</span>
                    </div>
                </div>
            )}
        </div>
    );
}
